import { Injectable } from '@angular/core';
import { OnboardingState } from '../config/state.model';

export interface TreeNode {
  text: string;
  dot: string;
  tag: string;
  bold?: boolean;
  children?: TreeNode[];
}

@Injectable({ providedIn: 'root' })
export class MappingService {

  getUniq(s: OnboardingState, fid: string, limit?: number): string[] {
    const col = s.maps[fid];
    if (!col) return [];
    const idx = s.cols.indexOf(col);
    if (idx < 0) return [];
    const vals = [...new Set(
      s.rows.map(r => String(r[idx] ?? '').trim()).filter(Boolean)
    )];
    return limit ? vals.slice(0, limit) : vals;
  }

  countUniq(s: OnboardingState, fid: string): number {
    return this.getUniq(s, fid).length;
  }

  countLinks(s: OnboardingState, fid: string): number {
    const col = s.maps[fid];
    if (!col) return 0;
    const idx = s.cols.indexOf(col);
    if (idx < 0) return 0;
    return s.rows.reduce((a, r) =>
      a + String(r[idx] ?? '').split(/[,;]/).filter(x => x.trim()).length, 0
    );
  }

  /** Counts rows where every one of the given fields has a non-empty mapped value. */
  countMappedRows(s: OnboardingState, fids: string[]): number {
    const idxs = fids.map(fid => {
      const col = s.maps[fid];
      return col ? s.cols.indexOf(col) : -1;
    });
    if (idxs.some(idx => idx < 0)) return 0;
    return s.rows.filter(row => idxs.every(idx => String(row[idx] ?? '').trim())).length;
  }

  buildPortfolioTree(s: OnboardingState): TreeNode[] {
    const p  = this.getUniq(s, 'portfolio');
    const sp = this.getUniq(s, 'subportfolio', 3);
    const pr = this.getUniq(s, 'program', 3);
    if (!p.length) return [];
    return p.map(v => ({
      text: v, dot: '#232459', tag: 'Portfolio', bold: true,
      children: sp.map(v2 => ({
        text: v2, dot: '#07b797', tag: 'Subportfolio',
        children: pr.map(v3 => ({ text: v3, dot: '#1ac9a8', tag: 'Program' }))
      }))
    }));
  }

  buildInitiativeTree(s: OnboardingState): TreeNode[] {
    const pr  = this.getUniq(s, 'linked_program');
    const ini = this.getUniq(s, 'initiative');
    if (!ini.length) return [];
    const programs = pr.length ? pr : ['(no program linked)'];
    return programs.map(v => ({
      text: v, dot: '#1ac9a8', tag: 'Program', bold: true,
      children: ini.map(v2 => ({ text: v2, dot: '#1a85d6', tag: 'Initiative' }))
    }));
  }

  buildRiskTree(s: OnboardingState): TreeNode[] {
    const colIdx = (fid: string) => {
      const col = s.maps[fid];
      return col ? s.cols.indexOf(col) : -1;
    };
    const domainColIdx    = colIdx('risk_domain');
    const subdomainColIdx = colIdx('risk_subdomain');
    const riskColIdx      = colIdx('risk');
    if (domainColIdx < 0 && riskColIdx < 0) return [];

    const domainMap = new Map<string, Map<string, Set<string>>>();
    for (const row of s.rows) {
      const domain    = String(row[domainColIdx] ?? '').trim();
      const subdomain = subdomainColIdx >= 0 ? String(row[subdomainColIdx] ?? '').trim() : '(General)';
      const risk      = riskColIdx >= 0 ? String(row[riskColIdx] ?? '').trim() : '';
      if (!domain) continue;
      if (!domainMap.has(domain)) domainMap.set(domain, new Map());
      const subdomainMap = domainMap.get(domain)!;
      if (!subdomainMap.has(subdomain)) subdomainMap.set(subdomain, new Set());
      if (risk) subdomainMap.get(subdomain)!.add(risk);
    }

    return [...domainMap.entries()].map(([domain, subdomainMap]) => ({
      text: domain, dot: '#1a7a6a', tag: 'Risk Domain', bold: true,
      children: [...subdomainMap.entries()].slice(0, 3).map(([subdomain, risks]) => ({
        text: subdomain, dot: '#2a9d87', tag: 'Risk Subdomain',
        children: [...risks].slice(0, 3).map(risk => ({ text: risk, dot: '#5b9cf6', tag: 'Risk' }))
      }))
    }));
  }

  buildOkrTree(s: OnboardingState): TreeNode[] {
    const obj  = this.getUniq(s, 'objective');
    const kr   = this.getUniq(s, 'key_result', 3);
    const goal = this.getUniq(s, 'goal', 3);
    if (!obj.length && !kr.length) return [];
    return (obj.length ? obj : ['(objective)']).map(v => ({
      text: v, dot: '#1a7a6a', tag: 'Objective', bold: true,
      children: kr.map(v2 => ({
        text: v2, dot: '#2a9d87', tag: 'Key Result',
        children: goal.map(v3 => ({ text: v3, dot: '#2a9d87', tag: 'Goal' }))
      }))
    }));
  }

  buildDeliveryObligationsTree(s: OnboardingState): TreeNode[] {
    const cats = this.getUniq(s, 'term_category');
    const subs = this.getUniq(s, 'term_subcategory', 3);
    const obs  = this.getUniq(s, 'obligation', 3);
    if (!cats.length && !obs.length) return [];
    return (cats.length ? cats : ['(category)']).map(v => ({
      text: v, dot: '#7c3aed', tag: 'Term Category', bold: true,
      children: subs.map(v2 => ({
        text: v2, dot: '#a78bfa', tag: 'Term Subcategory',
        children: obs.map(v3 => ({ text: v3, dot: '#c4b5fd', tag: 'Obligation' }))
      }))
    }));
  }

  buildCatalogOwnersTree(s: OnboardingState): TreeNode[] {
    const colIdx = (fid: string) => {
      const col = s.maps[fid];
      return col ? s.cols.indexOf(col) : -1;
    };
    const keyIdx   = colIdx('catalog_key');
    const subIdx   = colIdx('subkey');
    const ownerIdx = colIdx('owner_name');
    if (keyIdx < 0 || subIdx < 0) return [];

    const keyMap = new Map<string, Map<string, Set<string>>>();
    for (const row of s.rows) {
      const key   = String(row[keyIdx] ?? '').trim();
      const sub   = String(row[subIdx] ?? '').trim();
      const owner = ownerIdx >= 0 ? String(row[ownerIdx] ?? '').trim() : '';
      if (!key || !sub) continue;
      if (!keyMap.has(key)) keyMap.set(key, new Map());
      const subMap = keyMap.get(key)!;
      if (!subMap.has(sub)) subMap.set(sub, new Set());
      if (owner) subMap.get(sub)!.add(owner);
    }

    return [...keyMap.entries()].map(([key, subMap]) => ({
      text: key, dot: '#0891b2', tag: 'Catalog Key', bold: true,
      children: [...subMap.entries()].slice(0, 5).map(([sub, owners]) => ({
        text: sub, dot: '#22d3ee', tag: 'Subkey',
        children: [...owners].slice(0, 3).map(owner => ({ text: owner, dot: '#0e7490', tag: 'Owner' }))
      }))
    }));
  }

  buildFlatTree(s: OnboardingState, fid: string, dot: string, tag: string): TreeNode[] {
    const items = this.getUniq(s, fid);
    if (!items.length) return [];
    return items.map(v => ({ text: v, dot, tag, bold: true }));
  }

  computeCounts(moduleId: string, s: OnboardingState): number[] {
    switch (moduleId) {
      case 'portfolio':
        return ['portfolio', 'subportfolio', 'program'].map(id => this.countUniq(s, id));
      case 'initiative':
        return [this.countUniq(s, 'initiative'), this.countUniq(s, 'linked_program')];
      case 'risk': {
        const controlTypeCol = s.maps['control_type'];
        const controlTypeIdx = controlTypeCol ? s.cols.indexOf(controlTypeCol) : -1;
        const controlCount = controlTypeIdx >= 0
          ? s.rows.filter(r => String(r[controlTypeIdx] ?? '').trim()).length
          : 0;
        return [
          this.countUniq(s, 'risk_domain'),
          this.countUniq(s, 'risk_subdomain'),
          this.countUniq(s, 'risk'),
          controlCount,
        ];
      }
      case 'okr':
        return [this.countUniq(s, 'objective'), this.countUniq(s, 'key_result'), this.countUniq(s, 'goal')];
      case 'delivery_obligations':
        return [
          this.countUniq(s, 'term_category'),
          this.countUniq(s, 'term_subcategory'),
          this.countUniq(s, 'obligation'),
        ];
      case 'metrics':
        return [this.countUniq(s, 'metric_name')];
      case 'vendor_master':
        return [this.countUniq(s, 'name')];
      case 'tech_map_applications':
        return [this.countUniq(s, 'name')];
      case 'tech_map_services':
        return [this.countUniq(s, 'service_name')];
      case 'tech_map_infrastructures':
        return [this.countUniq(s, 'infrastructure_name')];
      case 'workforce_catalog':
        return [this.countUniq(s, 'resource_name')];
      case 'people_competency_map':
        return [this.countUniq(s, 'title')];
      case 'profit_cost_centres':
        return [this.countUniq(s, 'code')];
      case 'tbm_cost_pool':
        return [this.countUniq(s, 'cost_pool')];
      case 'tbm_it_tower':
        return [this.countUniq(s, 'it_resource_tower_name')];
      case 'tbm_service':
        return [this.countUniq(s, 'service_name')];
      case 'tbm_solution':
        return [this.countUniq(s, 'name')];
      case 'turnkey_plans':
        return [this.countUniq(s, 'name')];
      case 'module_owners':
        return [this.countUniq(s, 'zone_name')];
      case 'catalog_owners':
        return [this.countMappedRows(s, ['catalog_key', 'subkey', 'owner_name'])];
      default:
        return [];
    }
  }

  buildTree(moduleId: string, s: OnboardingState): TreeNode[] {
    switch (moduleId) {
      case 'portfolio':            return this.buildPortfolioTree(s);
      case 'initiative':           return this.buildInitiativeTree(s);
      case 'risk':                 return this.buildRiskTree(s);
      case 'okr':                  return this.buildOkrTree(s);
      case 'delivery_obligations': return this.buildDeliveryObligationsTree(s);
      case 'metrics':              return this.buildFlatTree(s, 'metric_name',   '#0891b2', 'Metric');
      case 'vendor_master':        return this.buildFlatTree(s, 'name',          '#b45309', 'Vendor');
      case 'tech_map_applications':    return this.buildFlatTree(s, 'name', '#0f766e', 'Application');
      case 'tech_map_infrastructures': return this.buildFlatTree(s, 'infrastructure_name', '#0f766e', 'Infrastructure');
      case 'tech_map_services':        return this.buildFlatTree(s, 'service_name', '#0f766e', 'Service');
      case 'workforce_catalog':    return this.buildFlatTree(s, 'resource_name', '#be185d', 'Resource');
      case 'people_competency_map': return this.buildFlatTree(s, 'title',         '#7c3aed', 'Competency');
      case 'profit_cost_centres':  return this.buildFlatTree(s, 'code',                    '#15803d', 'Centre');
      case 'tbm_cost_pool':        return this.buildFlatTree(s, 'cost_pool',               '#b45309', 'Cost Pool');
      case 'tbm_it_tower':         return this.buildFlatTree(s, 'it_resource_tower_name',  '#0f766e', 'IT Tower');
      case 'tbm_service':          return this.buildFlatTree(s, 'service_name',            '#7c3aed', 'Service');
      case 'tbm_solution':         return this.buildFlatTree(s, 'name',                    '#1a7a6a', 'Solution');
      case 'turnkey_plans':        return this.buildFlatTree(s, 'name',                    '#0d9488', 'Plan');
      case 'module_owners':        return this.buildFlatTree(s, 'zone_name',               '#7c3aed', 'Zone');
      case 'catalog_owners':       return this.buildCatalogOwnersTree(s);
      default:                     return [];
    }
  }
}
